import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { Product, ProductType } from '@inter-ikea-storageone/range';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { getUprights } from '@inter-ikea-storagetwo/boaxel/dist/utils/sectionsHelpers';
import { getMarketProducts, getProductGroups, getSelectedProducts } from 'ducks/products';
import { changeSelectedWidth } from '@inter-ikea-storagetwo/boaxel/dist/functions/ChangeSelectedWidth';
import { getRightUprightsOffsetFromLeft } from '@inter-ikea-storagetwo/boaxel/dist/functions/interactiveBoaxelSection';
import { useCore } from 'contexts/core';
import Title from 'ui/Typography/Title';

import { getFilterOptions, getUnit } from 'filters/SizeFilter/utils';
import AdjustableController from './AdjustableController';
import { isMobile } from 'utils/platform';

const MOUNTING_RAIL_MAX_WIDTH = 800;
const ADJUSTABLE_SIZE = '200';

const ChangeSelectedSectionWidth = () => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();

  const selectedProducts = useSelector(getSelectedProducts);
  const productGroups = useSelector(getProductGroups);
  const marketProducts = useSelector(getMarketProducts);

  const sectionProducts = marketProducts.filter(p =>
    productGroups['sections'].all.includes(p.coreProductId)
  );

  const sectionMountingRail = useMemo(
    () => selectedProducts.find(p => p.type === ProductType.BOAXEL_NON_MOUNTING_RAIL),
    [selectedProducts]
  );
  const [currentSize, setCurrentSize] = useState<number>();

  const filteredSizes = sectionProducts.filter(p => p.globalItemNo.includes('EXT'));
  const availableSizes = getFilterOptions('width')(filteredSizes);

  const unit = getUnit(sectionProducts);
  const adjustableSize = formatAdjustableSize(filteredSizes, unit);

  const sizes = Object.entries(availableSizes)
    .map(([size, dimensions]) => ({
      value: parseInt(size, 10),
      text: size === ADJUSTABLE_SIZE ? `${adjustableSize}` : `${dimensions} ${unit}`,
      unit: `${unit}`
    }))
    .filter(s => s.value <= MOUNTING_RAIL_MAX_WIDTH)
    .toReversed();

  const items = useMemo(
    () =>
      sizes.map(size => ({
        label: size.text,
        value: size.value
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!sectionMountingRail) return;
    const mountingRail = coreApi.entities.find(sectionMountingRail.entityId);

    if (!mountingRail) return;
    const { rightUpright } = getUprights(mountingRail);

    if (!rightUpright) return;
    const offset = getRightUprightsOffsetFromLeft(mountingRail, rightUpright);
    const roundedOffset = Math.round(offset / 10) * 10; // Round to nearest ten
    setCurrentSize(roundedOffset);
  }, [selectedProducts, sectionMountingRail, coreApi.entities]);

  const changeWidth = (size: number) => {
    const res = changeSelectedWidth(coreApi.core, size);
    res?.valid && setCurrentSize(size);
    return res;
  };

  if (!currentSize) return null;

  return (
    <>
      {!isMobile() && (
        <Title type={ThemeFontStyleTypeEnum.headingXSmall}>
          {formatMessage({ id: 'storageone.width' })}
        </Title>
      )}
      <AdjustableController
        items={items}
        unit={unit}
        currentSize={currentSize}
        setCurrentSize={setCurrentSize}
        onButtonClick={changeWidth}
        onSliderChange={changeWidth}
      />
    </>
  );
};

const formatAdjustableSize = (filteredSizes: Product[], unit: string) => {
  const comSizeText = filteredSizes.find(p => p.communicatedDimensions.width === 200)
    ?.communicatedSizeText;

  const dimensions = comSizeText?.split('x');

  if (!dimensions) return '';

  const width = dimensions.length === 1 ? dimensions[0] : dimensions[1];

  switch (unit) {
    case 'cm':
      return width;
    case 'in':
      return width.replace('"', unit);
  }
};

export default ChangeSelectedSectionWidth;
